import { useTranslation } from 'react-i18next';
import { useIssuerAnalytics, useMyDIDs, useRecentActivity } from '../../api/hooks';

// Issuer actions shown in this page's Recent Activity, with their icons.
const ISSUER_ACTIONS: Record<string, { icon: string; bg: string; color: string }> = {
  'Credential Issued': { icon: 'workspace_premium', bg: 'bg-blue-50', color: 'text-blue-600' },
  'Credential Revoked': { icon: 'block', bg: 'bg-red-50', color: 'text-red-600' },
  'Schema Created': { icon: 'schema', bg: 'bg-emerald-50', color: 'text-emerald-600' },
  'Schema Deactivated': { icon: 'pause_circle', bg: 'bg-amber-50', color: 'text-amber-600' },
};

/** Quote a value for CSV: wrap in quotes and double any quotes inside. */
function csvCell(v: unknown): string {
  return `"${String(v ?? '').replace(/"/g, '""')}"`;
}

export default function IssuerAnalytics() {
  // Analytics are keyed by the issuer's own DID — resolve it before querying
  // (the API has no `current` alias; sending that literal returns no rows).
  const { data: myDIDs, loading: didsLoading } = useMyDIDs();
  const issuerDid = (myDIDs ?? []).find((d) => d.status === 'active')?.did ?? (myDIDs ?? [])[0]?.did;
  const { data: analytics, loading: analyticsLoading, error } = useIssuerAnalytics(issuerDid);
  const loading = didsLoading || (!!issuerDid && analyticsLoading);
  // The analytics endpoint has no activity list; take the issuer's own actions
  // from their (already caller-scoped) activity feed.
  const { data: activityFeed } = useRecentActivity(50);
  const recentActivity = (activityFeed ?? [])
    .filter((a) => a.action in ISSUER_ACTIONS)
    .slice(0, 10)
    .map((a) => ({
      ...a,
      icon: ISSUER_ACTIONS[a.action].icon,
      iconBg: ISSUER_ACTIONS[a.action].bg,
      iconColor: ISSUER_ACTIONS[a.action].color,
    }));
  const { t } = useTranslation(['issuer', 'common']);

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-text-main">{t('analytics.title')}</h1>
            <p className="mt-1 text-sm text-text-muted">{t('analytics.subtitle')}</p>
          </div>
        </div>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  // No DID yet is a normal state for a new issuer — not a load failure.
  if (!issuerDid) {
    return (
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-text-main">{t('analytics.title')}</h1>
            <p className="mt-1 text-sm text-text-muted">{t('analytics.subtitle')}</p>
          </div>
        </div>
        <div className="rounded-xl border border-border-subtle bg-white py-20 text-center">
          <span className="material-symbols-outlined mb-3 text-4xl text-text-faint">bar_chart</span>
          <p className="text-sm font-medium text-text-main">No issuance analytics yet</p>
          <p className="mt-1 text-xs text-text-faint">Create a DID and issue your first credential to see analytics here.</p>
        </div>
      </div>
    );
  }

  if (error || !analytics) {
    return (
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-text-main">{t('analytics.title')}</h1>
            <p className="mt-1 text-sm text-text-muted">{t('analytics.subtitle')}</p>
          </div>
        </div>
        <div className="text-center py-20 text-red-500">{t('common:errors.loadAnalyticsFailed')}</div>
      </div>
    );
  }

  const { stats, monthlyData, topSchemas } = analytics;

  // A spreadsheet-ready report (opens in Excel), one section per panel.
  const exportReport = () => {
    const rows: unknown[][] = [
      ['IDA Issuer Analytics Report'],
      ['Issuer DID', issuerDid],
      ['Generated', new Date().toISOString()],
      [],
      ['Summary'],
      ['Metric', 'Value'],
      ...(stats ?? []).map((s) => [s.label, s.value]),
      [],
      ['Issuance Over Time'],
      ['Month', 'Credentials Issued'],
      ...(monthlyData ?? []).map((d) => [d.month, d.value]),
      [],
      ['Top Schemas'],
      ['Schema', 'Credentials Issued', '% of Total'],
      ...(topSchemas ?? []).map((sc) => [sc.name, sc.issued, `${sc.percentage}%`]),
      [],
      ['Recent Activity'],
      ['Time', 'Action', 'Detail'],
      ...recentActivity.map((a) => [a.timestamp, a.action, a.detail]),
    ];
    const csv = rows.map((r) => r.map(csvCell).join(',')).join('\r\n');
    // BOM so Excel reads the file as UTF-8.
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ida-issuer-analytics-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const maxValue = Math.max(...(monthlyData ?? []).map((d) => d.value), 1);

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-text-main">{t('analytics.title')}</h1>
          <p className="mt-1 text-sm text-text-muted">{t('analytics.subtitle')}</p>
        </div>
        <button className="btn-secondary" onClick={exportReport}>
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>download</span>
          {t('common:actions.exportReport')}
        </button>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
        {(stats ?? []).map((stat) => (
          <div key={stat.label} className="stat-card group">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold uppercase tracking-wider text-text-muted">{stat.label}</span>
              <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-xs font-bold text-emerald-600">
                <span className="material-symbols-outlined mr-0.5 align-middle" style={{ fontSize: 12 }}>trending_up</span>
                {stat.trend}
              </span>
            </div>
            <span className="mt-auto block text-3xl font-bold text-text-main">{stat.value}</span>
            <span
              className="material-symbols-outlined absolute -right-2 -top-2 text-slate-900 opacity-5 transition-opacity group-hover:opacity-10"
              style={{ fontSize: 96, fontVariationSettings: "'FILL' 1" }}
            >
              {stat.icon}
            </span>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="rounded-xl border border-border-subtle bg-white p-6 shadow-sm">
            <div className="mb-6 flex items-center gap-2">
              <span className="material-symbols-outlined text-text-muted" style={{ fontSize: 18 }}>bar_chart</span>
              <h2 className="text-sm font-extrabold uppercase tracking-widest text-text-main">{t('analytics.issuanceOverTime')}</h2>
            </div>
            <div className="flex items-end gap-4" style={{ height: 200 }}>
              {(monthlyData ?? []).map((d) => (
                <div key={d.month} className="flex flex-1 flex-col items-center gap-2">
                  <span className="text-[10px] font-bold text-text-muted">{d.value}</span>
                  <div
                    className="w-full rounded-t-lg bg-primary transition-all hover:bg-primary-dark"
                    style={{ height: `${(d.value / maxValue) * 160}px` }}
                  />
                  <span className="text-[10px] font-bold text-text-faint">{d.month}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div>
          <div className="rounded-xl border border-border-subtle bg-white p-6 shadow-sm">
            <div className="mb-6 flex items-center gap-2">
              <span className="material-symbols-outlined text-text-muted" style={{ fontSize: 18 }}>schema</span>
              <h2 className="text-sm font-extrabold uppercase tracking-widest text-text-main">{t('analytics.topSchemas')}</h2>
            </div>
            <div className="space-y-4">
              {(topSchemas ?? []).map((schema) => (
                <div key={schema.name}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-bold text-text-main">{schema.name}</span>
                    <span className="text-text-faint">{schema.issued.toLocaleString()}</span>
                  </div>
                  <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full rounded-full bg-primary" style={{ width: `${schema.percentage}%` }} />
                  </div>
                  <span className="text-[10px] text-text-faint">{t('analytics.percentOfTotal', { percent: schema.percentage })}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div>
        <div className="mb-4 flex items-center gap-2">
          <span className="material-symbols-outlined text-text-muted" style={{ fontSize: 18 }}>history</span>
          <h2 className="text-sm font-extrabold uppercase tracking-widest text-text-main">{t('analytics.recentActivity')}</h2>
        </div>
        <div className="rounded-xl border border-border-subtle bg-white shadow-sm">
          {recentActivity.length === 0 && (
            <p className="p-6 text-center text-sm text-text-faint">
              No issuer activity yet. Issued and revoked credentials and schema changes appear here.
            </p>
          )}
          {recentActivity.map((activity, idx) => (
            <div
              key={idx}
              className={`flex items-start gap-4 p-4 transition-colors hover:bg-slate-50/60 ${
                idx < recentActivity.length - 1 ? 'border-b border-border-subtle' : ''
              }`}
            >
              <div className={`flex size-10 shrink-0 items-center justify-center rounded-lg ${activity.iconBg}`}>
                <span className={`material-symbols-outlined ${activity.iconColor}`} style={{ fontSize: 20 }}>{activity.icon}</span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-text-main">{activity.action}</p>
                <p className="mt-0.5 truncate text-xs text-text-faint">{activity.detail}</p>
              </div>
              <span className="shrink-0 text-[10px] font-bold text-text-faint">{activity.time}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
